import type { DeepReadonly } from '@itandi/itandi-bb-ui';
import {
  Td,
  Tr,
  Dropdown,
  DropdownMenu,
  Box,
  Label,
  Modalv2,
  Button,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Alert
} from '@itandi/itandi-bb-ui';
import { toast } from 'react-toastify';
import { useCallback, useMemo, useState } from 'react';
import type {
  Notification,
  NotificationEdit,
  NotificationTargetType
} from '~/models/Notification';
import {
  ADDRESS_TYPE_TO_LABELS,
  COMPANY_TRIGGER_TYPE_SELECT_OPTIONS,
  DEAL_STATE_LABELS,
  DEAL_TYPE_LABELS,
  NOTIFY_TYPE_LABELS,
  SEND_TYPE_LABELS,
  TRIGGER_DATE_TYPE_LABELS,
  VENDOR_TRIGGER_TYPE_SELECT_OPTIONS
} from '~/models/Notification';
import { NotificationModal } from '~/components/common/notifications/settings/NotificationModal';
import { isSuccess } from '~/utils/api/api_handler';
import type { NskResponse } from '~/models/Api';
import type { Status } from '~/models/Status';
import type { Subtype } from '~/models/Subtype';
import type { LineUserAccount } from '~/models/LineUserAccount';
import { renderDropDownButton } from '~/components/common/parts/dropdown/DropDownButton';

type Props = DeepReadonly<{
  type: NotificationTargetType;
  statuses: Status[];
  subtypes: Subtype[] | null;
  lineUserAccounts: LineUserAccount[] | null;
  useLineNotification: boolean;
  notification: Notification;
  handleMutate: () => Promise<void>;
  handleUpdate: (
    notification: NotificationEdit,
    notificationId: number
  ) => Promise<NskResponse<void>>;
  handleDestroy: (notification: Notification) => Promise<NskResponse<void>>;
}>;

const getNotifyReservationDaysLabel = (days: number): string => {
  if (days === 0) {
    return '当日';
  }
  if (days < 0) {
    return `${days * -1}日前`;
  }
  return `${days}日後`;
};

const isBlank = (text: string | null): boolean => {
  if (text === null) {
    return true;
  }
  return text.trim() === '';
};

export const NotificationListRow: React.FC<Props> = ({
  type,
  statuses,
  subtypes,
  lineUserAccounts,
  useLineNotification,
  notification,
  handleMutate,
  handleUpdate,
  handleDestroy
}: Props) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const statusLabel = useMemo(() => {
    if (statuses === null || notification.triggerType !== 'status') {
      return '';
    }
    const status =
      statuses.find((s) => s.id === notification.triggerStatusId) ?? null;
    return status === null ? '' : status.name;
  }, [statuses, notification]);
  const handleUpdateAccept = useCallback(
    async (notificationEdit: NotificationEdit): Promise<void> => {
      const response = await handleUpdate(notificationEdit, notification.id);
      if (isSuccess(response)) {
        await handleMutate();
        setIsEditModalOpen(false);
        toast.success('通知設定を更新しました。');
        return;
      }
      toast.error('通知設定の更新に失敗しました。');
    },
    [handleMutate, notification, handleUpdate]
  );
  const handleDeleteAccept = useCallback(async () => {
    const response = await handleDestroy(notification);
    if (isSuccess(response)) {
      await handleMutate();
      setIsDeleteModalOpen(false);
      toast.success('通知設定を削除しました');
      return;
    }
    toast.error('エラーが発生しました');
  }, [notification, handleMutate, handleDestroy]);
  const subtype = subtypes?.find((s) => s.id === notification.dealSubtypeId);
  if (statuses === null) {
    return null;
  }
  const {
    triggerType,
    triggerDateType,
    notifyReservationDays,
    dealEstimationState,
    dealInspectionReportState,
    dealCompletionReportState,
    dealConstructionScheduleState
  } = notification;
  return (
    <>
      <Tr>
        <Td>
          <Box>{DEAL_TYPE_LABELS[notification.dealType]}</Box>
          {notification.dealType === 'other' &&
            type === 'CompanyNotification' && (
              <Box marginTop=".25rem">
                <Label color="subtle" size="xs">
                  {subtype?.name ?? ''}
                </Label>
              </Box>
            )}
        </Td>
        <Td>
          {type === 'CompanyNotification' && (
            <Box>{COMPANY_TRIGGER_TYPE_SELECT_OPTIONS[triggerType]?.label}</Box>
          )}
          {type === 'VendorNotification' && (
            <Box>{VENDOR_TRIGGER_TYPE_SELECT_OPTIONS[triggerType]?.label}</Box>
          )}
          {triggerType === 'status' && (
            <Box marginTop=".25rem">
              <Label color="subtle" size="xs">
                {statusLabel}
              </Label>
            </Box>
          )}
          {triggerType === 'specific_date' && triggerDateType && (
            <Box marginTop=".25rem">
              <Label color="subtle" size="xs">
                {TRIGGER_DATE_TYPE_LABELS[triggerDateType]}
              </Label>
            </Box>
          )}
        </Td>
        <Td>
          {dealEstimationState === 'no_setting' &&
            dealInspectionReportState === 'no_setting' &&
            dealCompletionReportState === 'no_setting' &&
            dealConstructionScheduleState === 'no_setting' && (
              <Box>登録状況に関わらず通知</Box>
            )}
          {dealEstimationState !== 'no_setting' && (
            <Box marginTop=".25rem">
              <Label size="xs">
                {`見積り: ${DEAL_STATE_LABELS[dealEstimationState]}`}
              </Label>
            </Box>
          )}
          {dealInspectionReportState !== 'no_setting' && (
            <Box marginTop=".25rem">
              <Label size="xs">
                {`立会報告画像: ${DEAL_STATE_LABELS[dealInspectionReportState]}`}
              </Label>
            </Box>
          )}
          {dealCompletionReportState !== 'no_setting' && (
            <Box marginTop=".25rem">
              <Label size="xs">
                {`完了報告画像: ${DEAL_STATE_LABELS[dealCompletionReportState]}`}
              </Label>
            </Box>
          )}
          {dealConstructionScheduleState !== 'no_setting' && (
            <Box marginTop=".25rem">
              <Label size="xs">
                {`工事日程の入力状況: ${DEAL_STATE_LABELS[dealConstructionScheduleState]}`}
              </Label>
            </Box>
          )}
        </Td>
        <Td>
          <Box>{NOTIFY_TYPE_LABELS[notification.notifyType]}</Box>
          {notification.notifyType === 'reservation' &&
            notifyReservationDays && (
              <Box marginTop=".25rem">
                <Label color="subtle" size="xs">
                  {getNotifyReservationDaysLabel(notifyReservationDays)}
                </Label>
              </Box>
            )}
        </Td>
        <Td>
          <Box>{SEND_TYPE_LABELS[notification.sendType]}</Box>
          {notification.sendType === 'mail' && (
            <Box marginTop=".25rem">
              <Label color="subtle" size="xs">
                {`${
                  ADDRESS_TYPE_TO_LABELS[
                    notification.notificationMails[0]?.addressType
                  ] ?? 'TODO担当者'
                }${notification.notificationMails.length > 1 ? ' 等' : ''}`}
              </Label>
            </Box>
          )}
        </Td>
        <Td>
          {isBlank(notification.title)
            ? notification.content
            : notification.title}
        </Td>
        <Td>
          <Dropdown button={renderDropDownButton('操作')}>
            <DropdownMenu
              onClick={() => {
                setIsEditModalOpen(true);
              }}
            >
              編集
            </DropdownMenu>
            <DropdownMenu danger onClick={() => setIsDeleteModalOpen(true)}>
              削除
            </DropdownMenu>
          </Dropdown>
        </Td>
      </Tr>
      {isEditModalOpen && (
        <NotificationModal
          handleAcceptSubmit={handleUpdateAccept}
          handleClose={() => setIsEditModalOpen(false)}
          lineUserAccounts={lineUserAccounts}
          notification={notification}
          statuses={statuses}
          subtypes={subtypes}
          type={type}
          useLineNotification={useLineNotification}
        />
      )}
      <Modalv2
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        size="s"
      >
        <ModalHeader title="通知設定の削除" />
        <ModalBody>
          通知を削除します。
          <Alert mt={16} variant="warning">
            この操作は元に戻せません。
          </Alert>
        </ModalBody>
        <ModalFooter
          leftArea={
            <Button
              onClick={() => {
                setIsDeleteModalOpen(false);
              }}
              variant="secondary"
            >
              キャンセル
            </Button>
          }
          rightArea={
            <Button onClick={handleDeleteAccept} variant="danger">
              削除
            </Button>
          }
        />
      </Modalv2>
    </>
  );
};
